/**
 * product-tabs.js
 *
 * Initialises all .cc-product-tabs sections on the page.
 * Load with:  <script src="{{ 'product-tabs.js' | asset_url }}" defer></script>
 *
 * CWV notes
 *  – Deferred: zero render-blocking.
 *  – Tab switches: class-toggle only, no forced layout reads inside the
 *    click handler (scrollIntoView is scheduled by the browser, not us).
 *  – ResizeObserver for read-more overflow check (no resize-event thrashing).
 *  – scrollIntoView with {block:'nearest'} centres active tab in the nav
 *    strip without triggering page scroll.
 *  – Accordion / read-more elements are found via class + `:scope`-style
 *    container queries, NOT by rebuilding an id from section.id — the
 *    Liquid markup keys those ids off block.id, not section.id, so a
 *    naive '#Details-RemainingIngredients-' + sectionId lookup silently
 *    fails and this script must not repeat that bug.
 */

(function () {
  'use strict';

  /* ─── Initialise a single section ────────────────────────────────── */
  function initProductTabs(container) {
    var tabs  = Array.from(container.querySelectorAll('.cc-product-tabs__tab'));
    var panes = Array.from(container.querySelectorAll('.cc-product-tabs__pane'));
    var nav   = container.querySelector('.cc-product-tabs__nav');

    /* ── Tab switching ─────────────────────────────────────────────── */
    function activateTab(targetId) {
      panes.forEach(function (pane) {
        pane.classList.toggle('is-active', pane.id === targetId);
      });

      tabs.forEach(function (tab) {
        var active = tab.getAttribute('data-tab') === targetId;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        activateTab(tab.getAttribute('data-tab'));

        /* Scroll active tab into view inside the nav strip.
           scrollIntoView with inline:'center' keeps the tab visible
           without moving the page viewport. */
        if (nav && typeof tab.scrollIntoView === 'function') {
          tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    });

    /* ── Ingredients accordion ARIA ────────────────────────────────── */
    /* Query by class within the container — every ingredients pane has
       at most one accordion, so this is unambiguous and doesn't depend
       on section.id vs block.id ever lining up. */
    var accordionDetails = container.querySelector('.cc-product-tabs__ingredients-accordion details');
    var accordionSummary = container.querySelector('.cc-product-tabs__accordion-summary');

    if (accordionSummary && accordionDetails) {
      accordionSummary.setAttribute('role', 'button');
      accordionSummary.setAttribute(
        'aria-expanded',
        accordionDetails.hasAttribute('open') ? 'true' : 'false'
      );

      accordionDetails.addEventListener('toggle', function () {
        accordionSummary.setAttribute(
          'aria-expanded',
          accordionDetails.hasAttribute('open') ? 'true' : 'false'
        );
      });
    }

    /* ── Why-choose-us: copy feature text to icon cells (mobile) ───── */
    container.querySelectorAll('.cc-product-tabs__why-row').forEach(function (row) {
      var titleCell = row.querySelector('.cc-product-tabs__why-cell--title');
      var iconCells = row.querySelectorAll('.cc-product-tabs__why-cell--icon');
      if (!titleCell || iconCells.length < 2) return;

      var text = titleCell.textContent.trim();

      [].forEach.call(iconCells, function (cell) {
        var hasCheck = !!cell.querySelector('.cc-product-tabs__icon--check');
        cell.setAttribute(
          'data-feature-text',
          hasCheck ? text : 'Not ' + text.charAt(0).toLowerCase() + text.slice(1)
        );
      });
    });

    /* ── Read-more toggle ──────────────────────────────────────────── */
    /* Query within container by class — description pane has at most
       one of each, so no id lookup (and no id-mismatch risk) needed. */
    var descContent = container.querySelector('.cc-product-tabs__description-content');
    var readMoreBtn = container.querySelector('.cc-product-tabs__read-more-btn');
    var descWrapper = container.querySelector('.cc-product-tabs__description-wrapper');

    if (!descContent || !readMoreBtn || !descWrapper) return;

    function checkOverflow() {
      /* Use computed line-height; fall back to 25 if not parseable */
      var lh = parseFloat(window.getComputedStyle(descContent).lineHeight) || 25;
      var overflows = descContent.scrollHeight > lh * 10 + 2; /* +2px tolerance */

      descWrapper.classList.toggle('has-overflow', overflows);
      readMoreBtn.style.display = overflows ? 'block' : 'none';
    }

    /* Initial check after fonts/layout are likely settled */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(checkOverflow);
    } else {
      checkOverflow();
    }

    /* ResizeObserver: re-check whenever the element's size changes
       (no resize-event thrashing / no unthrottled listener on modern
       browsers). */
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(checkOverflow).observe(descContent);
    } else {
      /* Throttled fallback for older browsers */
      var raf;
      window.addEventListener('resize', function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(checkOverflow);
      });
    }

    /* Toggle expanded/collapsed state */
    readMoreBtn.addEventListener('click', function () {
      var expanded     = descContent.classList.contains('is-expanded');
      var readMoreText = readMoreBtn.querySelector('.cc-product-tabs__read-more-text');
      var readLessText = readMoreBtn.querySelector('.cc-product-tabs__read-less-text');

      descContent.classList.toggle('is-expanded', !expanded);
      readMoreBtn.setAttribute('aria-expanded', expanded ? 'false' : 'true');

      if (readMoreText) readMoreText.style.display = expanded ? 'inline' : 'none';
      if (readLessText) readLessText.style.display = expanded ? 'none'   : 'inline';
    });
  }

  /* ─── Bootstrap: init every section on the page ─────────────────── */
  function init() {
    document.querySelectorAll('.cc-product-tabs').forEach(initProductTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());